// BA Breath Logo for the desk: the BeyondAge mark (two crossed infinity loops) with a breathing
// centre, atoms travelling the loops and corner dots, drawn on a transparent canvas.
// Same engine + settings as beyondage_crm frontend_changes/utils/baBreathLogo.js; keep them in sync.
// Tuning page: https://claude.ai/artifact/1GcqPEBRdjPPmNGjR5aMPh

// Settings approved for the product (4s expand, 2s hold, 4s contract, 1s rest).
export const BA_BREATH_LOGO_CONFIG = Object.freeze({
  shape: 'logo',
  weight: 0.2,
  dots: 'opposite',
  inhale: 4,
  holdIn: 2,
  exhale: 4,
  holdOut: 1,
  speed: 1,
  centerMin: 0.4,
  centerMax: 1.2,
  ringMode: 'breathe',
  ringAmp: 0.06,
  drift: 720, // deg/min, clockwise: one full turn every 30 s
  tilt: 0,
  atoms: true,
  atomsPerRing: 2,
  orbit: 8,
  atomDir: 'same',
  label: false,
  tint: '#a8dcb9',
})


// Name cards (desk identity card, CRM lead card): the same breath, drawn as thin bright lines
// with a stronger glow. The left sidebars keep BA_BREATH_LOGO_CONFIG's thick, soft rings.
export const BA_BREATH_LOGO_CARD = Object.freeze({
  ...BA_BREATH_LOGO_CONFIG,
  weight: 0.07,
  ringAlpha: 0.85,
  ringGlow: 0.22,
  tint: '#c4f0d3',
})

// How the loops are painted: opacity of the ring layer and its glow (× mark size).
// Missing values give the stock look (0.36 / 0.07), so older configs render unchanged.
export function ring_style(cfg) {
  var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };
  var a = typeof cfg.ringAlpha === "number" ? cfg.ringAlpha : 0.36;
  var g = typeof cfg.ringGlow === "number" ? cfg.ringGlow : 0.07;
  return { alpha: clamp(a, 0, 1), glow: clamp(g, 0, 1) };
}

export function BeyondageRings(canvas, opts) {
  var D = { shape: "logo", weight: 0.3, dots: "still",
            inhale: 4, holdIn: 2, exhale: 4, holdOut: 1, speed: 1, centerMin: 0.4, centerMax: 1.2,
            ringMode: "constant", ringAmp: 0.06, drift: 0, tilt: 0, atoms: true, atomsPerRing: 2,
            orbit: 8, atomDir: "alternate", label: false, tint: "#a8dcb9" };
  var cfg = Object.assign({}, D, opts || {});
  var ctx = canvas.getContext("2d");
  var w = 0, h = 0, raf = 0, playing = true, clock = 0, last = null, state = { v: 0, phase: "in", left: 0, t: 0, T: 1 };
  var paths = null, layer = document.createElement("canvas"), lctx = layer.getContext("2d");
  var ease = function (x) { return 0.5 - 0.5 * Math.cos(Math.PI * x); };
  function rgb(hx) { var n = parseInt(hx.replace("#", ""), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
  // The mark is two crossed infinity loops (a horizontal one and a vertical one).
  // Each petal is a teardrop: two straight sides meeting at 45 degrees in the centre,
  // closed by a 270-degree arc. Unit scale: centre to the outer tip of a petal = 1.
  function buildPaths() {
    var r = 1 / (1 + Math.SQRT2), c = r * Math.SQRT2, q = r * Math.SQRT1_2;
    function line(pts, x0, y0, x1, y1) { for (var i = 1; i <= 30; i++) pts.push([x0 + (x1 - x0) * i / 30, y0 + (y1 - y0) * i / 30]); }
    function arc(pts, ox, a0, a1) { for (var i = 1; i <= 90; i++) { var t = a0 + (a1 - a0) * i / 90; pts.push([ox + Math.cos(t) * r, Math.sin(t) * r]); } }
    var base = [[q, -q]];
    arc(base, c, -3 * Math.PI / 4, 3 * Math.PI / 4);   // right petal, round end
    line(base, q, q, -q, -q);                           // straight through the centre
    arc(base, -c, -Math.PI / 4, -7 * Math.PI / 4);      // left petal, round end
    line(base, -q, q, q, -q);                           // straight back through the centre
    return [0, Math.PI / 2].map(function (off) {
      var co = Math.cos(off), so = Math.sin(off);
      var pts = base.map(function (p) { return [p[0] * co - p[1] * so, p[0] * so + p[1] * co]; });
      var len = [0];
      for (var i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
      return { pts: pts, len: len, total: len[len.length - 1] };
    });
  }
  function at(path, d) {
    d = ((d % path.total) + path.total) % path.total;
    var L = path.len, lo = 0, hi = L.length - 1;
    while (hi - lo > 1) { var m = (lo + hi) >> 1; if (L[m] <= d) lo = m; else hi = m; }
    var f = (d - L[lo]) / ((L[hi] - L[lo]) || 1), a = path.pts[lo], b = path.pts[hi];
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  }
  function breath(t) {
    var a = cfg.inhale, b = cfg.holdIn, c = cfg.exhale, d = cfg.holdOut, T = a + b + c + d;
    if (T <= 0) return { v: 0, phase: "rest", left: 0, t: 0, T: 1 };
    var x = t % T, pos = x;
    if (x < a) return { v: ease(x / a), phase: "in", left: a - x, t: pos, T: T }; x -= a;
    if (x < b) return { v: 1, phase: "hold", left: b - x, t: pos, T: T }; x -= b;
    if (x < c) return { v: 1 - ease(x / c), phase: "out", left: c - x, t: pos, T: T }; x -= c;
    return { v: 0, phase: "rest", left: d - x, t: pos, T: T };
  }
  function resize() {
    var r = canvas.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = r.width; h = r.height;
    canvas.width = layer.width = Math.max(1, Math.round(w * dpr)); canvas.height = layer.height = Math.max(1, Math.round(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); lctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw();
  }
  function atom(x, y, ar, col) {
    ctx.save(); ctx.shadowColor = col(0.9); ctx.shadowBlur = ar * 4;
    ctx.fillStyle = "#effff3"; ctx.beginPath(); ctx.arc(x, y, ar, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  }
  function draw() {
    if (!w || !h) return;
    ctx.clearRect(0, 0, w, h);
    var c = rgb(cfg.tint), col = function (a) { return "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + a + ")"; };
    var S = Math.min(w, h), A = S * (cfg.shape !== "circles" ? 0.3 : 0.36), cx = w / 2, cy = h / 2;
    state = breath(clock); var v = state.v;
    var s = cfg.ringMode === "breathe" ? 1 + cfg.ringAmp * (v * 2 - 1) : 1;
    var rot = (cfg.tilt + cfg.drift * clock / 60) * Math.PI / 180, cr_ = Math.cos(rot), sr_ = Math.sin(rot);
    var tf = function (x, y) { return [cx + x * cr_ - y * sr_, cy + x * sr_ + y * cr_]; };
    var logo = cfg.shape !== "circles", R = A * 0.555, ar = A * 0.035;

    var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, A * 1.4);
    g.addColorStop(0, col(0.08 + 0.12 * v)); g.addColorStop(1, col(0));
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

    // Loops are drawn opaque on a layer and composited once, so crossings don't stack brighter.
    var L = lctx; L.clearRect(0, 0, w, h);
    L.lineWidth = A * cfg.weight; L.strokeStyle = col(1); L.lineJoin = "round"; L.lineCap = "round";
    if (logo) {
      if (!paths) paths = buildPaths();
      paths.forEach(function (p) {
        L.beginPath();
        p.pts.forEach(function (pt, i) { var q = tf(pt[0] * A * s, pt[1] * A * s); i ? L.lineTo(q[0], q[1]) : L.moveTo(q[0], q[1]); });
        L.closePath(); L.stroke();
      });
    } else {
      for (var k = 0; k < 4; k++) {
        var q = tf(Math.cos(-Math.PI / 2 + k * Math.PI / 2) * R * 0.97 * s, Math.sin(-Math.PI / 2 + k * Math.PI / 2) * R * 0.97 * s);
        L.beginPath(); L.arc(q[0], q[1], R * s, 0, Math.PI * 2); L.stroke();
      }
    }
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
    var rs = ring_style(cfg);
    ctx.globalAlpha = rs.alpha; ctx.shadowColor = col(0.5); ctx.shadowBlur = A * rs.glow * (canvas.width / w);
    ctx.drawImage(layer, 0, 0); ctx.restore();

    if (cfg.dots !== "hide") {
      var dv = cfg.dots === "breathe" ? v : cfg.dots === "opposite" ? 1 - v : 0.5;
      var dr = A * (logo ? 0.19 : 0.11) * (cfg.dots === "still" ? 1 : 0.7 + 0.6 * dv), dd = A * (logo ? 1.33 : 1.08) * s;
      ctx.fillStyle = col(cfg.dots === "still" ? 0.5 : 0.35 + 0.35 * dv);
      for (var k2 = 0; k2 < 4; k2++) {
        var a2 = Math.PI / 4 + k2 * Math.PI / 2, q2 = tf(Math.cos(a2) * dd, Math.sin(a2) * dd);
        ctx.beginPath(); ctx.arc(q2[0], q2[1], dr, 0, Math.PI * 2); ctx.fill();
      }
    }

    var cr = A * 0.18 * (cfg.centerMin + (cfg.centerMax - cfg.centerMin) * v);
    var halo = ctx.createRadialGradient(cx, cy, cr * 0.4, cx, cy, cr * 2.8);
    halo.addColorStop(0, col(0.28 + 0.3 * v)); halo.addColorStop(1, col(0));
    ctx.fillStyle = halo; ctx.beginPath(); ctx.arc(cx, cy, cr * 2.8, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = col(0.42 + 0.25 * v); ctx.beginPath(); ctx.arc(cx, cy, cr, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = Math.max(1, A * 0.011); ctx.strokeStyle = col(0.6); ctx.stroke();

    if (cfg.atoms) {
      var n = Math.max(1, Math.round(cfg.atomsPerRing)), orbit = Math.max(0.5, cfg.orbit);
      if (logo) {
        paths.forEach(function (p, k) {
          var dir = cfg.atomDir === "alternate" && k % 2 ? -1 : 1;
          for (var i = 0; i < n; i++) {
            var d = p.total * (i / n + k * 0.25) + dir * p.total * clock / orbit;
            for (var j = 12; j >= 1; j--) {
              var tp = at(p, d - dir * j * p.total * 0.008), tq = tf(tp[0] * A * s, tp[1] * A * s);
              ctx.fillStyle = col(0.32 * (1 - j / 13));
              ctx.beginPath(); ctx.arc(tq[0], tq[1], ar * (1 - j / 16), 0, Math.PI * 2); ctx.fill();
            }
            var hp = at(p, d), hq = tf(hp[0] * A * s, hp[1] * A * s);
            atom(hq[0], hq[1], ar, col);
          }
        });
      } else {
        for (var k3 = 0; k3 < 4; k3++) {
          var base = -Math.PI / 2 + k3 * Math.PI / 2, dir3 = cfg.atomDir === "alternate" && k3 % 2 ? -1 : 1;
          var ox = Math.cos(base) * R * 0.97 * s, oy = Math.sin(base) * R * 0.97 * s;
          for (var i3 = 0; i3 < n; i3++) {
            var th = base + Math.PI + dir3 * (Math.PI * 2 * clock / orbit) + i3 * Math.PI * 2 / n + k3 * 0.6;
            for (var j3 = 10; j3 >= 1; j3--) {
              var tj = th - dir3 * j3 * 0.045, qq = tf(ox + Math.cos(tj) * R * s, oy + Math.sin(tj) * R * s);
              ctx.fillStyle = col(0.32 * (1 - j3 / 11));
              ctx.beginPath(); ctx.arc(qq[0], qq[1], ar * (1 - j3 / 14), 0, Math.PI * 2); ctx.fill();
            }
            var hq3 = tf(ox + Math.cos(th) * R * s, oy + Math.sin(th) * R * s);
            atom(hq3[0], hq3[1], ar, col);
          }
        }
      }
    }

    if (cfg.label) {
      var words = { in: "Breathe in", hold: "Hold", out: "Breathe out", rest: "Rest" };
      ctx.font = "500 " + Math.round(S * 0.042) + "px Figtree, system-ui, sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = col(0.85);
      ctx.fillText(words[state.phase] + "  " + Math.ceil(state.left), cx, Math.min(h - S * 0.04, cy + A * 1.28));
    }
  }
  // The loop only runs while there is motion to show: playing, on screen and the tab visible.
  // Anything else stops it (the last drawn frame stays on the canvas); kick() restarts it.
  var visible = true, io = null;
  function running() { return playing && visible && !document.hidden; }
  function kick() {
    if (!raf && running()) { last = null; raf = requestAnimationFrame(loop); }
  }
  function loop(ts) {
    raf = 0;
    if (!running()) return;
    if (last === null) last = ts;
    var dt = Math.min((ts - last) / 1000, 0.1); last = ts;
    clock += dt * cfg.speed;
    draw(); raf = requestAnimationFrame(loop);
  }
  var ro = new ResizeObserver(resize); ro.observe(canvas); resize();
  if (typeof IntersectionObserver !== "undefined") {
    io = new IntersectionObserver(function (entries) {
      visible = entries[entries.length - 1].isIntersecting; kick();
    });
    io.observe(canvas);
  }
  document.addEventListener("visibilitychange", kick);
  kick();
  return {
    set: function (o) { Object.assign(cfg, o); draw(); },
    play: function () { playing = true; kick(); },
    pause: function () { playing = false; draw(); },
    isPlaying: function () { return playing; },
    restart: function () { clock = 0; if (!raf) draw(); },
    state: function () { return state; },
    config: function () { return Object.assign({}, cfg); },
    destroy: function () {
      cancelAnimationFrame(raf); raf = 0; ro.disconnect(); if (io) io.disconnect();
      document.removeEventListener("visibilitychange", kick);
    }
  };
}
